import { ArrowLeftLine, ArrowRightLine } from '@/icons'
import { useRef, useState, useEffect, ReactNode } from 'react'
import { useSwipeable } from 'react-swipeable'
import { IconButton } from '../../atoms'
import { Absolute, Relative, Row } from '../../styled'
import { CarouselItem, Container, Step, CarouselViewport } from './styles'

export interface CarouselProps {
  children: ReactNode | ReactNode[]
  width: number
  autoPlayDuration?: number
}

export const Carousel = ({
  children,
  width,
  autoPlayDuration,
}: CarouselProps) => {
  const [active, setActive] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const childrenArray = Array.isArray(children) ? children : [children]

  const handleChange = (i: number) => {
    const carousel = ref.current
    if (!carousel) return

    let newActive = i

    if (i < 0) {
      newActive = -1
    }
    if (i > childrenArray.length - 1) {
      newActive = childrenArray.length
    }
    carousel.style.transition = 'transform 500ms ease'

    setActive(newActive)

    if ([-1, childrenArray.length].includes(newActive)) {
      setTimeout(() => {
        if (carousel) {
          carousel.style.transition = ''
          if (newActive === -1) {
            setActive(childrenArray.length - 1)
          } else {
            setActive(0)
          }
        }
      }, 500)
    }
  }

  const handleNext = () => {
    handleChange(active + 1)
  }

  const handlePrev = () => {
    handleChange(active - 1)
  }

  const handlers = useSwipeable({
    trackMouse: true,
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrev,
  })

  let timeout: NodeJS.Timeout | undefined

  useEffect(() => {
    if (timeout) clearTimeout(timeout)

    if (autoPlayDuration) {
      // TODO timeout variable is outside of the useEffect scope and is not permanent and seem to be an issue
      // kept this here to avoid logic change but needs to be fixed
      timeout = setTimeout(() => {
        handleChange(active + 1)
      }, autoPlayDuration)
    }

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [active, autoPlayDuration])

  return (
    <Relative>
      <Container $width={width}>
        <CarouselViewport
          $active={-1 * (active + 1)}
          $width={width}
          {...handlers}
          ref={ref}
        >
          <CarouselItem>{childrenArray[childrenArray.length - 1]}</CarouselItem>
          {childrenArray.map((item, index) => (
            <CarouselItem key={`carousel-item-${index}`}>{item}</CarouselItem>
          ))}
          <CarouselItem>{childrenArray[0]}</CarouselItem>
        </CarouselViewport>
      </Container>
      <Absolute left="-60px" top="50%">
        <IconButton onClick={handlePrev} size="small" variant="outlined">
          <ArrowLeftLine />
        </IconButton>
      </Absolute>
      <Absolute right="-60px" top="50%">
        <IconButton onClick={handleNext} size="small" variant="outlined">
          <ArrowRightLine />
        </IconButton>
      </Absolute>
      <Absolute bottom="-20px" left={0} right={0}>
        <Row gap="4px" justifyContent="center">
          {childrenArray.map((_, i) => (
            <Step
              key={`carousel-step-${i}`}
              onClick={() => {
                handleChange(i)
              }}
              selected={i === active}
            />
          ))}
        </Row>
      </Absolute>
    </Relative>
  )
}
